<script>
import 'quill/dist/quill.bubble.css'
import 'quill/dist/quill.core.css'

import debounce from 'lodash/debounce'
import formatRelative from 'date-fns/formatRelative'
import parseISO from 'date-fns/parseISO'
import PopConfirm from '@/components/menus/PopConfirm.vue'
import { useSpineStore } from '@/stores/SpineStore'
import QuillEditor from '@/components/spine/QuillEditor.vue'

export default {
  components: {
    PopConfirm,
    QuillEditor,
  },
  props: {
    entry: {
      type: Object,
      required: true,
    },
  },
  emits: ['focusModeOn', 'focusModeOff', 'imageLoaded'],
  setup() {
    return {
      spineStore: useSpineStore(),
    }
  },
  data() {
    return {
      content: '',
      focusMode: false,
      editing: false,
      editorOptions: {
        theme: 'bubble',
        placeholder: 'Add some thoughts...',
        modules: {
          toolbar: [['bold', 'italic', 'underline', 'strike', 'blockquote', 'link']],
          magicUrl: true,
        },
      },
      dialogOpen: false,
      showConfirmDelete: false,
    }
  },
  computed: {
    timestamp() {
      return formatRelative(parseISO(this.entry.created_at), new Date())
    },
    entryImageUrl() {
      let imageUrl = this.entry.image_original
      if (this.entry.image_thumbnail_small)
        imageUrl = this.entry.image_thumbnail_small
      else if (this.entry.image_thumbnail_large)
        imageUrl = this.entry.image_thumbnail_large

      return imageUrl
    },
  },
  created() {
    this.debouncedAutoSave = debounce(this.autoSave, 500)
    this.content = this.entry.text_content
  },
  unmounted() {
    this.debouncedAutoSave.cancel()
  },
  methods: {
    autoSave() {
      if (this.content !== this.entry.text_content && this.content) {
        // Make sure content has changed and that the content is not blank. The content should
        // never be blank on autoupdate.
        this.handleSave()
      }
    },
    handleSave() {
      const bookBoardEntry = {
        entryId: this.entry.id,
        textContent: this.content,
      }
      this.spineStore.updateBookBoardEntry(bookBoardEntry)
        .catch(() => {
          const message = 'An error occurred while autosaving your entry. Please check your connection.'
          this.spineStore.setAlert({
            type: 'error',
            message: `${message}`,
            active: true,
          })
        })
    },
    handleDelete() {
      this.dialogOpen = false
      this.spineStore.removeBookBoardEntry(this.entry.id)
        .catch(() => {
          const message = 'An error occurred while deleting your entry. Please try again.'
          this.spineStore.setAlert({
            type: 'error',
            message: `${message}`,
            active: true,
          })
        })
    },
    handleFocusModeOn() {
      this.$emit('focusModeOn')
      setTimeout(() => {
        this.focusMode = true
      }, 400)
    },
    handleFocusModeOff() {
      this.focusMode = false
      setTimeout(() => {
        this.$emit('focusModeOff')
      }, 400)
    },
    handleImageLoad() {
      this.$emit('imageLoaded')
    },
    onEditorBlur() {
      this.editing = false
    },
    onEditorFocus() {
      this.editing = true
    },
    onEditorReady() {
    },
  },
}
</script>

<template>
  <div
    :id="`book-board-entry-${entry.id}`"
    class="entry"
    :class="{ 'focus-mode': focusMode, 'dialog-open': dialogOpen }"
  >
    <div
      class="entry__doc"
      :class="{ editing, 'dialog-open': dialogOpen }"
    >
      <div class="action-btns">
        <span
          v-if="!focusMode"
          class="oi action-btns__expand-icon"
          data-glyph="fullscreen-enter"
          @click="handleFocusModeOn"
        />
        <span
          v-if="focusMode"
          class="oi action-btns__de-expand-icon"
          data-glyph="fullscreen-exit"
          @click="handleFocusModeOff"
        />
        <PopConfirm
          class="action-btns__delete-btn"
          :unique-el-id="entry.id"
          icon-type="delete"
          ok-text="Yes"
          cancel-text="Cancel"
          horz-align="align-right"
          vert-align="align-top"
          confirm-message="Are you sure you want to delete this entry?"
          @show-dialog="dialogOpen = true"
          @hide-dialog="dialogOpen = false"
          @ok="handleDelete"
        />
      </div>
      <p class="entry__doc__timestamp">
        {{ timestamp }}
      </p>
      <QuillEditor
        v-model="content"
        :content="content"
        :options="editorOptions"
        handle=".drag_handle"
        :disabled="!editing"
        @blur="onEditorBlur($event)"
        @focus="onEditorFocus($event)"
        @ready="onEditorReady($event)"
        @change="debouncedAutoSave"
      />
      <div v-if="entryImageUrl" class="entry__doc__attachment">
        <img :src="entryImageUrl" @load="handleImageLoad">
      </div>
    </div>
  </div>
</template>

<style lang='scss'>
$action-btn-width: 2.4rem;

.entry {
  position: relative;
  border-radius: 0.3rem;
  padding-left: 3.5rem;
  margin-left: -3.5rem;
  margin-top: 1rem;
  transform: translate3d(0, 0, 0);

  .ql-editor {
    padding: 0;
  }

  &.dialog-open {
    z-index: 2;
  }

  &__doc {
    padding: 1.5rem 2.5rem;
    background-color: $color-white;
    border: 1px solid rgb(239 238 237 / 0%);
    border-radius: 0.3rem;
    box-shadow: none;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.17, 0.67, 0.83, 0.67);

    .action-btns {
      position: absolute;
      right: 0;
      top: 0.8rem;
      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
      align-items: center;

      &__expand-icon,
      &__de-expand-icon {
        @include font-size(1);

        width: $action-btn-width;
        text-align: center;
        color: $color-action-button;
        opacity: 0;
        transition: all 0.2s cubic-bezier(0.17, 0.67, 0.83, 0.67);

        @include respond-to('medium') {
          display: none;
        }
      }

      &__delete-btn {
        @include font-size(1.3);

        position: relative;
        text-align: center;
        width: $action-btn-width;
        transition: all 0.2s cubic-bezier(0.17, 0.67, 0.83, 0.67);
        opacity: 0;

        @include respond-to('medium') {
          right: 0;
        }
      }
    }

    &__timestamp {
      color: $color-dark-gray;
      font-size: 1.1rem;
      margin: 0;
      width: 75%;
    }

    p, blockquote, em, strong {
      &::selection {
        background: #fff0f0; /* Gecko Browsers */
      }
    }

    .ql-editor {
      p, h1, h2, h3, h4, h5, h6, i, em, strong {
        cursor: pointer;
      }

      &.ql-blank {
        padding: 0;

        &::before {
          color: $color-gray-dark;
          font-style: normal;
          left: 0;
        }
      }
    }

    &.editing,
    &.dialog-open {
      background-color: $color-white;
      border: 1px solid rgb(239 238 237 / 100%);
      box-shadow: 1px 2px 7px 0 rgb(0 0 0 / 7%);
      z-index: 2;

      p, blockquote, em, strong {
        color: $color-gray-dark;
      }

      .ql-editor {
        p, h1, h2, h3, h4, h5, h6, i, em, strong {
          cursor: text;
        }
      }

      .action-btns__expand-icon,
      .action-btns__de-expand-icon,
      .action-btns__delete-btn {
        opacity: 1;
      }

      &:hover {
        background-color: $color-white;

        .action-btns__expand-icon,
        .action-btns__de-expand-icon,
        .action-btns__delete-btn {
          opacity: 1;
        }
      }
    }

    &__attachment {
      max-width: 60%;
      border: 1px solid $color-gainsboro;
      border-radius: 0.3rem;
      margin: 1.35rem 0;
      padding: 0.5rem;
    }
  }

  &:hover {
    .entry__doc {
      background-color: $color-white-smoke-light;

      &.editing {
        background-color: $color-white;
      }

      .action-btns {
        &__expand-icon,
        &__de-expand-icon {
          opacity: 0.6;

          &:hover {
            opacity: 1;
            color: $color-black;
          }
        }

        &__delete-btn {
          opacity: 1;
        }
      }
    }
  }

  &.focus-mode {
    position: fixed;
    top: 0;
    left: 31.5rem;
    padding: 0;
    margin-top: 0;
    width: calc(100% - 28rem);
    height: 100%;
    z-index: 20;
    box-shadow: -3px 1px 7px 0 rgb(0 0 0 / 4%);

    .entry__doc {
      height: 100%;
      background-color: $color-white;

      .drag_handle {
        opacity: 0;
        visibility: hidden;
      }

      &__timestamp {
        opacity: 0.5;
      }

      .quill-editor {
        margin-top: 3rem;
        padding: 5rem;

        .ql-container {
          @include font-size(1.59);
        }
      }

      .action-btns {
        &__expand-icon,
        &__de-expand-icon {
          opacity: 0.6;

          &:hover {
            opacity: 1;
            color: $color-black;
          }
        }

        &__delete-btn {
          opacity: 0;
          visibility: hidden;
          transition: none;
        }
      }
    }

    &:hover {
      background-color: $color-white;

      .entry__doc {
        background-color: $color-white;
      }
    }
  }
}
</style>
